import { useEffect, useRef, useState } from "react";
import { cluster } from "../Lib/KMeans";
import {
  Color,
  Counted,
  Image,
  imageToColors,
  mapPixels,
  remapColors,
} from "../Lib/Image";
import ColorBox from "./ColorBox";
import "./ImageEditor.css";
import { useEventListener } from "../Hooks/EventListener";

type ImageEditorProps = {
  bitmap: ImageBitmap;
  onChange?: (image: Image) => void;
};

export default function ImageEditor({ bitmap, onChange }: ImageEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maxWidth = Math.min(bitmap.width, 300);
  const maxHeight = Math.min(bitmap.height, 300);
  const [width, setWidth] = useState(maxWidth);
  const [height, setHeight] = useState(maxHeight);
  const [lockAspect, setLockAspect] = useState(true);
  const [paletteSize, setPaletteSize] = useState(10);
  const [palette, setPalette] = useState<Counted<Color>[]>([]);

  const updateWidth = (value: number) => {
    setWidth(value);
    if (widthRef.current) {
      widthRef.current.valueAsNumber = value;
    }
  };

  const updateHeight = (value: number) => {
    setHeight(value);
    if (heightRef.current) {
      heightRef.current.valueAsNumber = value;
    }
  };

  const updateLockAspect = (value: boolean) => {
    setLockAspect(value);
    if (value) {
      updateWidth(Math.ceil((bitmap.width * height) / bitmap.height));
    }
  };

  const updatePaletteSize = (value: number) => {
    setPaletteSize(value);
    if (paletteSizeRef.current) {
      paletteSizeRef.current.valueAsNumber = value;
    }
  };

  const widthRef = useEventListener<HTMLInputElement>("change", (ev) => {
    const width = Math.min(
      maxWidth,
      Math.max(1, (ev.target as HTMLInputElement).valueAsNumber)
    );
    updateWidth(width);
    if (lockAspect) {
      updateHeight(Math.ceil((bitmap.height * width) / bitmap.width));
    }
  });

  const heightRef = useEventListener<HTMLInputElement>("change", (ev) => {
    const height = Math.min(
      maxHeight,
      Math.max(1, (ev.target as HTMLInputElement).valueAsNumber)
    );
    updateHeight(height);
    if (lockAspect) {
      updateWidth(Math.ceil((bitmap.width * height) / bitmap.height));
    }
  });

  const paletteSizeRef = useEventListener<HTMLInputElement>("change", (ev) => {
    const paletteSize = Math.min(
      50,
      Math.max(2, (ev.target as HTMLInputElement).valueAsNumber)
    );
    updatePaletteSize(paletteSize);
  });

  const reset = () => {
    const width = maxWidth;
    const height = Math.ceil((bitmap.height * width) / bitmap.width);
    setLockAspect(true);
    updateWidth(width);
    updateHeight(height);
    updatePaletteSize(10);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset(), []);

  // resets on bitmap change
  useEffect(() => {
    // console.log("bitmap has changed, resetting editor");
    reset();
  },[bitmap]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    /*
    const worker = new Worker(
      new URL("../Workers/ImageWorker.ts", import.meta.url)
    );
    worker.postMessage(bitmap);
    */

    const offscreenCanvas = new OffscreenCanvas(width, height);
    const offscreenContext2d = offscreenCanvas.getContext(
      "2d"
    ) as OffscreenCanvasRenderingContext2D;
    if (offscreenContext2d === null) {
      return;
    }
    offscreenContext2d.imageSmoothingEnabled = false;

    // TODO allow the bitmap to be cropped
    const sx = 0;
    const sy = 0;
    const sw = bitmap.width;
    const sh = bitmap.height;

    offscreenContext2d.drawImage(
      bitmap,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      offscreenCanvas.width,
      offscreenCanvas.height
    );

    const imageData = offscreenContext2d.getImageData(
      0,
      0,
      offscreenCanvas.width,
      offscreenCanvas.height
    );

    const colors = Array.from(imageToColors(imageData))
      .sort((a, b) => b[1] - a[1])
      .map(([color, count]) => ({ count, value: color }));
    const { map, palette } = cluster(paletteSize, colors, true);
    const pixels = mapPixels(imageData, map);

    remapColors(
      imageData.data,
      pixels,
      palette.map((entry) => entry.value)
    );

    offscreenContext2d.putImageData(imageData, 0, 0);

    const canvas = canvasRef.current;
    canvas.width = 4 * width; //bitmap.width;
    canvas.height = 4 * height; // bitmap.height;

    const context2d = canvas.getContext("2d");
    if (context2d !== null) {
      context2d.imageSmoothingEnabled = false;
      context2d.drawImage(offscreenCanvas, 0, 0, canvas.width, canvas.height);
    }

    setPalette(palette);

    if (onChange) {
      onChange({
        width,
        height,
        palette,
        pixels,
      });
    }
  }, [bitmap, paletteSize, width, height, onChange]);

  return (
    <div>
      <div className="Image-ctrl">
        <label htmlFor="width">Width:</label>
        <input id="width" type="number" ref={widthRef} min={1} max={maxWidth} />
        <label htmlFor="height">Height:</label>
        <input
          id="height"
          type="number"
          ref={heightRef}
          min={1}
          max={maxHeight}
        />
        <label htmlFor="lock">Lock Aspect Ratio:</label>
        <input
          id="lock"
          type="checkbox"
          checked={lockAspect}
          onChange={(ev) => updateLockAspect(ev.target.checked)}
        />
        <label htmlFor="colors">Colors:</label>
        <input
          id="colors"
          type="number"
          ref={paletteSizeRef}
          min={2}
          max={40}
        />
        <button className="Image-reset" onClick={reset}>
          Reset
        </button>
      </div>

      <div className="Image-palette">
        {[...palette]
          .sort((a, b) => b.count - a.count || a.value - b.value)
          .map(({ count, value }, index) => (
            <ColorBox key={index} className="App-palette" color={value}>
              {count} {/*("000000" + value.toString(16)).substr(-6)*/}
            </ColorBox>
          ))}
      </div>
      <canvas className="Image-canvas" ref={canvasRef} />
      <h2 className="Image-crochets">{width * height} crochets</h2>
    </div>
  );
}
